/**
 * TravailStubPage
 * ─────────────────────────────────────────────────────────────────
 * Template éditorial des 9 pages travaux Particulier :
 *  /particuliers/travaux/[slug] (pompe-a-chaleur, isolation-combles, …)
 *
 * Refonte 2026 : entièrement bâti sur le kit UI `audience/particulier`.
 * Conserve la side card spécifique `TravailLivePreview` (illustration
 * data-dense par opération) qui reste contextuelle.
 *
 * Garde aussi 2 sections custom propres aux travaux :
 *  - Le panneau "Aides cumulables détaillé" (grille spécifique
 *    `cumulableWith` avec slugs vers /particuliers/aides/[slug])
 *  - Le panneau "Fiche CEE officielle" (référence ficheCode + lien)
 *
 * Exporte aussi `TravailMiniCard` (réutilisée par le hub /particuliers/travaux).
 */

import { ArrowRight, ExternalLink, Sparkles } from 'lucide-react';
import { Container } from '../layout/Container';
import { Button } from '../ui/button';
import { Reveal } from '../motion/Reveal';
import { StaggerGrid, StaggerItem } from '../motion/StaggerGrid';
import { CountUp } from '../motion/CountUp';
import { BreadcrumbListLd } from '../seo/jsonld/BreadcrumbList';
import { getIcon } from '../../lib/lucide-icons';
import { cn } from '../../lib/cn';
import { CUMUL_TONE_CLASSES, type TravailStub } from '../../config/travaux-particuliers';
import {
  PARTICULIER_GRADIENT,
  PARTICULIER_SECTION,
  PARTICULIER_URLS,
  ParticulierCTAFinal,
  ParticulierFAQ,
  ParticulierKPIBand,
  ParticulierKeyPointsGrid,
  ParticulierPageHero,
  ParticulierSectionHeader,
  ParticulierVerticalStepper,
  type ParticulierKpi,
} from '../audience/particulier';
import { TravailLivePreview } from './TravailLivePreview';

interface Props {
  travail: TravailStub;
}

const SITE_URL = PARTICULIER_URLS.site;

const CATEGORY_LABEL: Record<TravailStub['category'], string> = {
  isolation: 'Isolation',
  chauffage: 'Chauffage & ECS',
  ecs: 'Eau chaude sanitaire',
  ventilation: 'Ventilation',
};

export function TravailStubPage({ travail }: Props) {
  const costMid = Math.round((travail.avgCostEur.min + travail.avgCostEur.max) / 2);

  const kpi: ParticulierKpi[] = [
    {
      value: `−${travail.savingsPercent}%`,
      numericValue: travail.savingsPercent,
      suffix: '%',
      label: travail.savingsLabel,
      source: 'Selon situation initiale du logement',
    },
    {
      value: `${travail.maxAidEur.toLocaleString('fr-FR')} €`,
      numericValue: travail.maxAidEur,
      suffix: ' €',
      label: "d'aides cumulables max",
      source: "Cumul MaPrimeRénov’ + CEE + Coup de pouce",
    },
    {
      value: `${travail.avgCostEur.min.toLocaleString('fr-FR')} – ${travail.avgCostEur.max.toLocaleString('fr-FR')} €`,
      label: 'Coût indicatif TTC posé',
      source: 'Fourchette observée · varie selon configuration',
    },
  ];

  return (
    <>
      <BreadcrumbListLd
        items={[
          { name: 'Accueil', url: `${SITE_URL}/` },
          { name: 'Particuliers', url: `${SITE_URL}/particuliers` },
          { name: 'Travaux', url: `${SITE_URL}/particuliers/travaux` },
          {
            name: travail.title,
            url: `${SITE_URL}/particuliers/travaux/${travail.slug}`,
          },
        ]}
      />

      <ParticulierPageHero
        breadcrumb={[
          { name: 'Travaux', href: '/particuliers/travaux' },
          { name: travail.title },
        ]}
        eyebrow={travail.eyebrow}
        eyebrowIconName={travail.iconName}
        eyebrowTrailingNote={travail.ficheCode}
        headline={travail.title}
        subheadline={
          <>
            <span
              className={cn(
                'block font-display text-xl font-bold leading-tight md:text-2xl',
                PARTICULIER_GRADIENT.headline,
              )}
            >
              {travail.effyPromise}
            </span>
            <span className="mt-4 block text-[15px] leading-relaxed text-stone-600">
              {travail.lead}
            </span>
          </>
        }
        primaryCta={{ label: travail.ctaLabel, href: travail.ctaHref }}
        secondaryCta={travail.secondaryCta}
        bullets={[
          'Devis gratuit · sans engagement',
          'Artisan RGE certifié près de chez vous',
          'Aides chiffrées en 2 min',
        ]}
        microPromise={travail.comfortBenefit}
        sideSlot={<TravailLivePreview travail={travail} />}
      />

      <ParticulierKPIBand items={kpi} />

      <ParticulierKeyPointsGrid
        eyebrow="L'essentiel"
        title="Ce qu'il faut savoir avant de se lancer"
        points={travail.keyPoints}
        cols={2}
        align="center"
      />

      <ParticulierVerticalStepper
        eyebrow="Déroulement"
        title="Comment ça se passe"
        description={
          <>
            Du devis à la facture finale, voici les 4 étapes clés. Notre rôle : engager vos
            primes <span className="font-semibold text-ink">avant</span> la signature du devis pour
            ne perdre aucune aide.
          </>
        }
        steps={travail.steps}
        cta={{ label: 'Calculer mes aides personnalisées', href: travail.ctaHref }}
      />

      {/* ──── Section spécifique travaux : aides cumulables détaillées ──── */}
      <section className={PARTICULIER_SECTION.standard}>
        <Container>
          <ParticulierSectionHeader
            eyebrow="Aides mobilisables"
            title={
              <>
                Jusqu&apos;à{' '}
                <span className={PARTICULIER_GRADIENT.headline}>
                  <CountUp end={travail.maxAidEur} suffix=" €" />
                </span>{' '}
                d&apos;aides sur {travail.shortLabel}
              </>
            }
            description="Toutes les aides ci-dessous se cumulent sur la même opération. Notre calculateur chiffre votre cumul exact en 2 minutes."
            rightSlot={
              <a
                href="/particuliers/aides"
                className="inline-flex items-center gap-1.5 text-sm font-semibold text-brand-700 transition-colors hover:text-brand-800"
              >
                Voir le détail des aides
                <ArrowRight className="h-3.5 w-3.5" strokeWidth={2} />
              </a>
            }
          />

          <StaggerGrid className="mt-10 grid gap-3 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5">
            {travail.cumulableWith.map((aide) => {
              const classes = CUMUL_TONE_CLASSES[aide.tone];
              const inner = (
                <div className="group flex h-full flex-col gap-2.5 rounded-2xl border border-stone-200 bg-paper p-4 transition-all duration-200 ease-out hover:-translate-y-0.5 hover:border-stone-300 hover:shadow-[0_8px_24px_-8px_rgba(10,10,10,.08)]">
                  <span
                    className={cn(
                      'inline-flex h-8 w-8 items-center justify-center rounded-lg ring-1 ring-inset',
                      classes.chip,
                    )}
                  >
                    <Sparkles className="h-3.5 w-3.5" strokeWidth={1.8} />
                  </span>
                  <p className="font-display text-sm font-bold text-ink">{aide.label}</p>
                  <p className="text-[11.5px] leading-snug text-stone-500">{aide.detail}</p>
                  {aide.slug && (
                    <p className="mt-auto inline-flex items-center gap-1 pt-2 text-[11px] font-semibold text-brand-700 transition-transform duration-150 ease-out group-hover:translate-x-0.5">
                      En savoir plus
                      <ArrowRight className="h-3 w-3" strokeWidth={2.4} />
                    </p>
                  )}
                </div>
              );
              return (
                <StaggerItem key={aide.label}>
                  {aide.slug ? (
                    <a href={`/particuliers/aides/${aide.slug}`} className="block h-full">
                      {inner}
                    </a>
                  ) : (
                    inner
                  )}
                </StaggerItem>
              );
            })}
          </StaggerGrid>
        </Container>
      </section>

      {/* ──── Section spécifique travaux : fiche CEE officielle ──── */}
      <section className="border-t border-stone-100 bg-stone-50/40 py-16 md:py-20">
        <Container>
          <Reveal>
            <div className="mx-auto max-w-4xl rounded-2xl border border-stone-200 bg-gradient-to-br from-stone-50 via-paper to-stone-50 p-6 md:p-8">
              <div className="flex flex-col gap-6 sm:flex-row sm:items-center sm:justify-between">
                <div className="min-w-0 flex-1">
                  <div className="mb-2 flex flex-wrap items-center gap-2">
                    <span className="inline-flex items-center gap-1 rounded-md border border-brand-200 bg-brand-50 px-2 py-0.5 font-mono text-[11px] font-bold tracking-widest text-brand-700">
                      {travail.ficheCode}
                    </span>
                    <span className="eyebrow text-stone-500">Référentiel officiel</span>
                  </div>
                  <p className="font-display text-lg font-bold text-ink">
                    Fiche CEE {travail.ficheCode}
                  </p>
                  <p className="mt-1 text-[13.5px] leading-relaxed text-stone-600">
                    {travail.ficheLabel} — source Ministère de la Transition écologique. Détails
                    techniques, conditions exactes, mode de calcul officiel et profils éligibles.
                  </p>
                </div>
                <div className="shrink-0">
                  <Button asChild size="lg" variant="outline">
                    <a
                      href={`/cee/fiches/${travail.ficheCode}`}
                      className="inline-flex items-center gap-2"
                    >
                      Voir la fiche officielle
                      <ExternalLink className="h-3.5 w-3.5" />
                    </a>
                  </Button>
                </div>
              </div>
            </div>
          </Reveal>
        </Container>
      </section>

      <ParticulierFAQ
        eyebrow={`FAQ ${travail.shortLabel}`}
        title={`Vos questions sur ${travail.shortLabel}.`}
        items={travail.faq}
      />

      <ParticulierCTAFinal
        badge="Simulation 2 min · gratuite"
        title="Combien va vraiment vous coûter"
        titleAccent={`${travail.shortLabel} ?`}
        description={
          <>
            Estimation gratuite et chiffrée de votre cumul d&apos;aides : MaPrimeRénov&apos; + CEE +
            éco-PTZ + TVA 5,5 % en 2 minutes — pour un coût travaux moyen de{' '}
            {costMid.toLocaleString('fr-FR')} €.
          </>
        }
        primaryCta={{ label: travail.ctaLabel, href: travail.ctaHref }}
        secondaryCta={{ label: 'Parler à un conseiller', href: PARTICULIER_URLS.contact }}
        bullets={['Sans engagement', 'Aucune relance commerciale', 'Aides 2026 à jour']}
      />
    </>
  );
}

/* ════════════════════════════════════════════════════════════════
   Mini card réutilisée par /particuliers/travaux — version Effy
   ════════════════════════════════════════════════════════════════ */

export function TravailMiniCard({ travail }: Props) {
  const Icon = getIcon(travail.iconName);
  return (
    <a
      href={`/particuliers/travaux/${travail.slug}`}
      className="group relative flex h-full flex-col gap-3 overflow-hidden rounded-2xl border border-stone-200 bg-paper p-6 shadow-[0_1px_3px_rgba(10,10,10,.04)] transition-all duration-200 ease-out hover:-translate-y-0.5 hover:border-stone-300 hover:shadow-[0_12px_36px_-12px_rgba(10,10,10,.12)]"
    >
      <div
        aria-hidden
        className={cn(
          'absolute inset-x-0 top-0 h-px',
          PARTICULIER_GRADIENT.cardTopHairline,
        )}
      />

      <div className="flex items-center justify-between">
        <span
          className={cn(
            'inline-flex h-11 w-11 items-center justify-center rounded-xl text-brand-700 ring-1 ring-inset ring-brand-200/60 transition-transform duration-200 ease-out group-hover:scale-105',
            PARTICULIER_GRADIENT.iconHalo,
          )}
        >
          <Icon className="h-5 w-5" strokeWidth={1.7} />
        </span>
        <span className="font-mono text-[10px] font-bold tracking-widest text-stone-400">
          {travail.ficheCode}
        </span>
      </div>

      <div>
        <p className="font-display text-lg font-bold text-ink">{travail.title}</p>
        <p
          className={cn(
            'mt-1 text-[13px] font-bold tracking-tight',
            'bg-gradient-to-r from-brand-700 to-accent-600 bg-clip-text text-transparent',
          )}
        >
          {travail.effyPromise}
        </p>
      </div>

      <p className="line-clamp-2 text-[12.5px] leading-relaxed text-stone-600">
        {travail.comfortBenefit}
      </p>

      <div className="mt-auto grid grid-cols-2 gap-2 pt-2">
        <div className="rounded-lg bg-stone-50 px-2.5 py-2">
          <p className="text-[9px] font-semibold uppercase tracking-[0.12em] text-stone-400">
            Aides max
          </p>
          <p className="mt-0.5 font-display text-sm font-bold text-ink tabular-nums">
            {travail.maxAidEur.toLocaleString('fr-FR')} €
          </p>
        </div>
        <div className="rounded-lg bg-stone-50 px-2.5 py-2">
          <p className="text-[9px] font-semibold uppercase tracking-[0.12em] text-stone-400">
            Économies
          </p>
          <p className="mt-0.5 font-display text-sm font-bold text-brand-700 tabular-nums">
            −{travail.savingsPercent}%
          </p>
        </div>
      </div>

      <div className="flex items-center justify-between pt-1">
        <span className="text-[10px] uppercase tracking-[0.12em] text-stone-400">
          {CATEGORY_LABEL[travail.category]}
        </span>
        <span className="inline-flex items-center gap-1 text-[12px] font-semibold text-brand-700 transition-transform duration-150 ease-out group-hover:translate-x-0.5">
          Devis gratuit
          <ArrowRight className="h-3 w-3" strokeWidth={2.4} />
        </span>
      </div>
    </a>
  );
}
